import * as THREE from "three";
import type { Vec3, WaypointGraph } from "@rietz/shared";
import { makeLabel, type Label } from "./labels.js";
import {
  WAYPOINT_DEGREE_COLORS,
  WAYPOINT_EDGE_COLOR,
  WAYPOINT_EDGE_OPACITY,
  WAYPOINT_LABEL_DIST_M,
  WAYPOINT_LABEL_MAX,
  WAYPOINT_NODE_RADIUS,
} from "./palette.js";

/**
 * F4 — the scav navigation graph, for the two graph bugs invisible in the data:
 * a node buried in a wall and an unintended dead end (`validateMap` catches
 * islands). One InstancedMesh plus one LineSegments — two draw calls; the id
 * sprites are one call each, so they are built lazily within
 * `WAYPOINT_LABEL_DIST_M` and capped.
 */

export interface WaypointViz {
  group: THREE.Group;
  nodeCount: number;
  /** Edges actually drawn — ones referencing a missing node are skipped. */
  edgeCount: number;
  /** Id sprites built so far (always 0 without a DOM). */
  readonly labelCount: number;
  /** Currently visible id sprites. */
  readonly visibleLabels: number;
  /**
   * Refreshes label visibility against the camera. Allocation-free and
   * throttled.
   */
  update(cameraPos: THREE.Vector3, dtMs: number): void;
  dispose(): void;
}

/** Labels only need to keep up with walking speed, not with the display. */
const LABEL_REFRESH_MS = 100;

/** Scratch — `update` and the build both run without allocating. */
const dummy = new THREE.Object3D();
const nodeColor = new THREE.Color();

export function buildWaypointViz(graph: WaypointGraph): WaypointViz {
  const group = new THREE.Group();
  group.name = "debug:waypoints";

  const nodes = graph.nodes;
  const nodeCount = nodes.length;

  // --- Degrees, computed from the edge list rather than trusted: the point is
  // to
  // show what the edges say.
  const index = new Map<number, number>();
  for (let i = 0; i < nodeCount; i++) {
    const nd = nodes[i];
    if (nd !== undefined && !index.has(nd.id)) index.set(nd.id, i);
  }
  const degree = new Uint16Array(nodeCount);

  const edgePositions: number[] = [];
  let edgeCount = 0;
  for (const e of graph.edges) {
    const ia = index.get(e[0]);
    const ib = index.get(e[1]);
    if (ia === undefined || ib === undefined || ia === ib) continue;
    const a = nodes[ia];
    const b = nodes[ib];
    if (a === undefined || b === undefined) continue;
    degree[ia] = (degree[ia] ?? 0) + 1;
    degree[ib] = (degree[ib] ?? 0) + 1;
    edgePositions.push(a.pos.x, a.pos.y + 0.1, a.pos.z);
    edgePositions.push(b.pos.x, b.pos.y + 0.1, b.pos.z);
    edgeCount += 1;
  }

  // --- Nodes ---------------------------------------------------------------
  const nodeGeo = new THREE.OctahedronGeometry(WAYPOINT_NODE_RADIUS);
  const nodeMat = new THREE.MeshBasicMaterial({
    transparent: true,
    opacity: 0.92,
    depthWrite: false,
  });
  const instances = new THREE.InstancedMesh(nodeGeo, nodeMat, Math.max(1, nodeCount));
  instances.name = "waypoint-nodes";
  instances.count = nodeCount;
  instances.frustumCulled = false;

  for (let i = 0; i < nodeCount; i++) {
    const nd = nodes[i];
    if (nd === undefined) continue;
    dummy.position.set(nd.pos.x, nd.pos.y + 0.1, nd.pos.z);
    dummy.rotation.set(0, 0, 0);
    dummy.scale.setScalar(1);
    dummy.updateMatrix();
    instances.setMatrixAt(i, dummy.matrix);
    nodeColor.setHex(colorForDegree(degree[i] ?? 0));
    instances.setColorAt(i, nodeColor);
  }
  instances.instanceMatrix.needsUpdate = true;
  if (instances.instanceColor !== null) instances.instanceColor.needsUpdate = true;
  group.add(instances);

  // --- Edges ---------------------------------------------------------------
  const edgeGeo = new THREE.BufferGeometry();
  edgeGeo.setAttribute(
    "position",
    new THREE.BufferAttribute(new Float32Array(edgePositions), 3),
  );
  edgeGeo.computeBoundingSphere();
  const edgeMat = new THREE.LineBasicMaterial({
    color: WAYPOINT_EDGE_COLOR,
    transparent: true,
    opacity: WAYPOINT_EDGE_OPACITY,
    depthWrite: false,
  });
  const edgeLines = new THREE.LineSegments(edgeGeo, edgeMat);
  edgeLines.name = "waypoint-edges";
  edgeLines.frustumCulled = false;
  group.add(edgeLines);

  // --- Id labels (lazy) ----------------------------------------------------
  const labels: (Label | null)[] = new Array<Label | null>(nodeCount).fill(null);
  /** `false` once we know a node's label can never be built (no DOM). */
  const buildable = new Uint8Array(nodeCount).fill(1);
  let built = 0;
  let visible = 0;
  let sinceRefreshMs = LABEL_REFRESH_MS;

  const maxDistSq = WAYPOINT_LABEL_DIST_M * WAYPOINT_LABEL_DIST_M;

  return {
    group,
    nodeCount,
    edgeCount,
    get labelCount(): number {
      return built;
    },
    get visibleLabels(): number {
      return visible;
    },
    update(cameraPos: THREE.Vector3, dtMs: number): void {
      sinceRefreshMs += dtMs;
      if (sinceRefreshMs < LABEL_REFRESH_MS) return;
      sinceRefreshMs = 0;

      let shown = 0;
      for (let i = 0; i < nodeCount; i++) {
        const nd = nodes[i];
        if (nd === undefined) continue;
        const existing = labels[i] ?? null;

        const want = shown < WAYPOINT_LABEL_MAX && near(cameraPos, nd.pos, maxDistSq);
        if (!want) {
          if (existing !== null) existing.sprite.visible = false;
          continue;
        }
        shown += 1;

        if (existing !== null) {
          existing.sprite.visible = true;
          continue;
        }
        if (buildable[i] !== 1) continue;

        const label = makeLabel(String(nd.id), { heightM: 0.32, fontPx: 32 });
        if (label === null) {
          // No DOM (headless smoke test): stop asking for this one.
          buildable[i] = 0;
          continue;
        }
        label.sprite.position.set(nd.pos.x, nd.pos.y + 0.62, nd.pos.z);
        group.add(label.sprite);
        labels[i] = label;
        built += 1;
      }
      visible = shown;
    },
    dispose(): void {
      for (let i = 0; i < labels.length; i++) {
        labels[i]?.dispose();
        labels[i] = null;
      }
      built = 0;
      visible = 0;
      group.remove(instances, edgeLines);
      instances.dispose();
      nodeGeo.dispose();
      nodeMat.dispose();
      edgeGeo.dispose();
      edgeMat.dispose();
    },
  };
}

// ---------------------------------------------------------------------------

function near(cam: THREE.Vector3, p: Vec3, maxDistSq: number): boolean {
  const dx = cam.x - p.x;
  const dy = cam.y - p.y;
  const dz = cam.z - p.z;
  return dx * dx + dy * dy + dz * dz <= maxDistSq;
}

export function colorForDegree(degree: number): number {
  const last = WAYPOINT_DEGREE_COLORS.length - 1;
  const i = degree < 0 ? 0 : degree > last ? last : degree;
  return WAYPOINT_DEGREE_COLORS[i] ?? WAYPOINT_DEGREE_COLORS[0];
}
